import React, { useEffect, useState } from 'react';
import { Card, Button, Row, Col, message, Spin, Empty, Drawer, Tooltip, Tag, Input, Avatar, Select } from 'antd';
import { PlusOutlined, LikeOutlined, DislikeOutlined, MessageOutlined, CloseOutlined, UserOutlined } from '@ant-design/icons';
import ProjectServices from '../../services/projectServices';
import FeatureRequestForm from './FeatureRequestForm';
import styles from './FeatureRequest.module.css';
import TimeAgo from 'timeago-react';
import { getTranslation } from '../../util/utilFunc';
import { useSelector } from 'react-redux';

const { Option } = Select;

const FeatureRequestPage = () => {
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const { language } = useSelector((state) => state.auth);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [isCurrentChatOpen, setIsCurrentChatOpen] = useState("");
  const [comment, setComment] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("all"); // State for selected category
  const [selectedType, setSelectedType] = useState("all"); // State for selected category
  const [sortedFeatures, setSortedFeatures] = useState([]);


  useEffect(() => {
    if (!features) return; // Early return if features is undefined or null

    let sortedData = [];

    if (selectedType === 'most_popular') {
      sortedData = [...features].sort((a, b) => b.upvotes - a.upvotes);
    } else if (selectedType === 'least_popular') {
      sortedData = [...features].sort((a, b) => a.upvotes - b.upvotes);
    } else if (selectedType === 'most_discussed') {
      sortedData = [...features].sort((a, b) => b.comments.length - a.comments.length);
    } else if (selectedType === 'least_discussed') {
      sortedData = [...features].sort((a, b) => a.comments.length - b.comments.length);
    } else if (selectedType === 'most_recent') {
      sortedData = [...features].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    } else if (selectedType === 'oldest') {
      sortedData = [...features].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    } else {
      sortedData = [];
    }

    setSortedFeatures(sortedData);
  }, [selectedType, features]);

  useEffect(() => {
    fetchFeatures();
  }, []);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const response = await ProjectServices.getAllFeatures();
      setFeatures(response.data); // Assuming the API response contains the feature list in `data`
    } catch (error) {
      message.error(getTranslation("Failed to load features", language));
    } finally {
      setLoading(false);
    }
  };

  const handleVote = async (featureId, voteType) => {
    try {
      if (voteType === 'up') {
        await ProjectServices.upvoteFeature(featureId);
      } else {
        await ProjectServices.downvoteFeature(featureId);
      }
      message.success(`${getTranslation("Successfully", language)} ${voteType === 'up' ? getTranslation("upvoted", language) : getTranslation("downvoted", language)
        } ${getTranslation("the feature", language)}`);
      fetchFeatures(); // Refetch features to update vote counts
    } catch (error) {
      message.error(getTranslation("Failed to cast vote", language));

    }
  };

  if (loading) {
    return <div style={{ width: "100%", display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
      <Spin size="large" />
    </div>;
  }

  const toggleDrawer = () => {
    setDrawerVisible(!drawerVisible); // Toggle the drawer visibility
  };

  const getCurrentOpenedChat = () => {
    return features.find(item => item?.id === isCurrentChatOpen);
  };

  const handleSubmitComment = async () => {
    try {
      let data = {
        feature: isCurrentChatOpen,
        content: comment
      };
      const response = await ProjectServices.createIdeaComment(data);
      if (response.data) {
        message.success(getTranslation("Added New Comment Successfully!", language));
        fetchFeatures(); // Refetch features to update vote counts
      }
    } catch (error) {
      message.error(getTranslation("Failed to cast vote", language));
    }
  };


  return (
    <div>
      <header className={styles.featureRequest__header}>
        <h2>{getTranslation("Idea Requests", language)}</h2>
        <div className={styles.action__wrapper}>
          <div className={styles.filter__wrapper}>
            <label>{getTranslation("Filter By", language)}</label>
            <Select
              placeholder={getTranslation("Select a category", language)}
              value={selectedCategory}
              style={{ width: 200, marginRight: 16 }} // Styling for the Select component
              onChange={value => setSelectedCategory(value)} // Set selected category
            >
              <Option value="all">{getTranslation("All", language)}</Option>
              <Option value="feature_request">{getTranslation("Feature Request", language)}</Option>
              <Option value="bug_or_issue">{getTranslation("Bug or Issue", language)}</Option>
              <Option value="user_experience_improvement">{getTranslation("User Experience Improvement", language)}</Option>
              <Option value="small_enhancement">{getTranslation("Small Enhancement", language)}</Option>
              <Option value="big_idea">{getTranslation("Big Ideas", language)}</Option>
              <Option value="tool_integration">{getTranslation("Tool Integration", language)}</Option>
              <Option value="performance_improvement">{getTranslation("Performance Improvement", language)}</Option>
              <Option value="learning_support">{getTranslation("Learning and Support", language)}</Option>
              <Option value="team_collaboration">{getTranslation("Team Collaboration", language)}</Option>
              <Option value="other_suggestion">{getTranslation("Other Suggestions", language)}</Option>
            </Select>
          </div>
          <div className={styles.filter__wrapper}>
            <label>{getTranslation("Sort By", language)}</label>
            <Select
              placeholder={getTranslation("Sort By", language)}
              value={selectedType}
              style={{ width: 200, marginRight: 16 }} // Styling for the Select component
              onChange={value => setSelectedType(value)} // Set selected category
            >
              <Option value="all">{getTranslation("All", language)}</Option>
              <Option value="most_popular">{getTranslation("Most Popular", language)}</Option>
              <Option value="least_popular">{getTranslation("Least Popular", language)}</Option>
              <Option value="most_discussed">{getTranslation("Most Discussed", language)}</Option>
              <Option value="least_discussed">{getTranslation("Least Discussed", language)}</Option>
              <Option value="most_recent">{getTranslation("Most Recent", language)}</Option>
              <Option value="oldest">{getTranslation("Oldest", language)}</Option>
            </Select>
          </div>
          <Button type="primary" icon={<PlusOutlined />} onClick={toggleDrawer}>{getTranslation("Add Feature Request", language)}</Button>
        </div>
      </header>
      <br />
      {features.length === 0 ? (
        <Empty description={getTranslation("No Feature found!", language)} />
      ) : (
        sortedFeatures.length > 0 ?
          sortedFeatures
            .filter(feature => selectedCategory === "all" ? true : (!selectedCategory || feature?.additional_details?.category === selectedCategory)) // Filter by selected category
            .map((feature) => (
              <Row key={feature.id} style={{ marginBottom: '16px' }}>
                <Col span={24}>
                  <Card style={{ boxShadow: '0px 0.133px 4px lightgray' }} title={
                    <div className={styles.feature__cardHeader}>
                      <div>
                        <h3 style={{ marginBottom: "0px" }}>{feature.title}</h3>
                        <small style={{ color: 'gray' }}><TimeAgo datetime={feature?.created_at} /></small>
                      </div>
                      <div className={styles.feature__cardHeaderInfo}>
                        {feature?.additional_details?.category && <Tag color="purple">{feature?.additional_details?.category?.toUpperCase()}</Tag>}
                        <div>
                          <Tooltip title={getTranslation("Upvote?", language)}>
                            <Button
                              icon={<LikeOutlined />}
                              onClick={() => handleVote(feature.id, 'up')}
                            >
                              {feature.upvotes}
                            </Button>
                          </Tooltip>
                          <Tooltip title={getTranslation("Downvote?", language)}>
                            <Button
                              style={{ marginLeft: "10px" }}
                              icon={<DislikeOutlined />}
                              onClick={() => handleVote(feature.id, 'down')}
                            >
                              {feature.downvotes}
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  } bordered={true}>
                    <p>{feature.description}</p>
                    <div className={styles.footer}>
                      <div className={styles.tag__wrapper}>
                        {feature?.additional_details?.tags?.map((tag) => (
                          <Tag
                            key={tag}
                            style={{
                              cursor: 'pointer',
                              border: '1px solid #d9d9d9',
                              borderRadius: '5px',
                              padding: '5px 10px',
                              marginBottom: '8px',
                            }}
                          >
                            {tag}
                          </Tag>
                        ))}
                      </div>

                      <div>
                        <Tooltip title={getTranslation("Comment", language)}>
                          <Button onClick={() => setIsCurrentChatOpen(feature?.id)} icon={<MessageOutlined style={{ fontSize: '1.2rem' }} />}>
                            {feature?.comments?.length || 0}
                          </Button>
                        </Tooltip>
                      </div>
                    </div>

                    {isCurrentChatOpen === feature?.id &&
                      <Card title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <b>{getTranslation("Comment", language)}</b>
                          <Tooltip title={getTranslation("Close Comments?", language)}><Button onClick={() => setIsCurrentChatOpen("")} danger icon={<CloseOutlined />}></Button></Tooltip>
                        </div>
                      }>
                        <div>
                          <Input.TextArea placeholder={getTranslation('Enter your comment...', language)} onChange={(e) => setComment(e.target.value)} />
                          <Button style={{ marginTop: '10px' }} type="primary" size='small' onClick={handleSubmitComment}>{getTranslation("Comment", language)}</Button>
                        </div>
                        {(getCurrentOpenedChat() && getCurrentOpenedChat()?.comments?.length) ?
                          <div>
                            {getCurrentOpenedChat()?.comments?.map((item) => {
                              return (
                                <div className={styles.comment__wrapper}>
                                  <div className='topHeader'>
                                    <div>
                                      <Avatar icon={<UserOutlined />} />
                                      <b style={{ marginLeft: "10px" }}>{item?.user}</b>
                                    </div>
                                    <small>
                                      <TimeAgo datetime={item?.created_at} />
                                    </small>
                                  </div>
                                  <p>{item?.content}</p>
                                </div>
                              )
                            })}
                          </div>
                          :
                          <Empty description={getTranslation("No Comments!", language)} />
                        }
                      </Card>
                    }
                  </Card>
                </Col>
              </Row>
            )) :
          features
            .filter(feature => selectedCategory === "all" ? true : (!selectedCategory || feature?.additional_details?.category === selectedCategory)) // Filter by selected category
            .map((feature) => (
              <Row key={feature.id} style={{ marginBottom: '16px' }}>
                <Col span={24}>
                  <Card style={{ boxShadow: '0px 0.133px 4px lightgray' }} title={
                    <div className={styles.feature__cardHeader}>
                      <div>
                        <h3 style={{ marginBottom: "0px" }}>{feature.title}</h3>
                        <small style={{ color: 'gray' }}><TimeAgo datetime={feature?.created_at} /></small>
                      </div>
                      <div className={styles.feature__cardHeaderInfo}>
                        {feature?.additional_details?.category && <Tag color="purple">{feature?.additional_details?.category?.toUpperCase()}</Tag>}
                        <div>
                          <Tooltip title={getTranslation("Upvote?", language)}>
                            <Button
                              icon={<LikeOutlined />}
                              onClick={() => handleVote(feature.id, 'up')}
                            >
                              {feature.upvotes}
                            </Button>
                          </Tooltip>
                          <Tooltip title={getTranslation("Downvote?", language)}>
                            <Button
                              style={{ marginLeft: "10px" }}
                              icon={<DislikeOutlined />}
                              onClick={() => handleVote(feature.id, 'down')}
                            >
                              {feature.downvotes}
                            </Button>
                          </Tooltip>
                        </div>
                      </div>
                    </div>
                  } bordered={true}>
                    <p>{feature.description}</p>
                    <div className={styles.footer}>
                      <div className={styles.tag__wrapper}>
                        {feature?.additional_details?.tags?.map((tag) => (
                          <Tag
                            key={tag}
                            style={{
                              cursor: 'pointer',
                              border: '1px solid #d9d9d9',
                              borderRadius: '5px',
                              padding: '5px 10px',
                              marginBottom: '8px',
                            }}
                          >
                            {tag}
                          </Tag>
                        ))}
                      </div>

                      <div>
                        <Tooltip title={getTranslation("Comment", language)}>
                          <Button onClick={() => setIsCurrentChatOpen(feature?.id)} icon={<MessageOutlined style={{ fontSize: '1.2rem' }} />}>
                            {feature?.comments?.length || 0}
                          </Button>
                        </Tooltip>
                      </div>
                    </div>

                    {isCurrentChatOpen === feature?.id &&
                      <Card title={
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <b>{getTranslation("Comment", language)}</b>
                          <Tooltip title={getTranslation("Close Comments?", language)}><Button onClick={() => setIsCurrentChatOpen("")} danger icon={<CloseOutlined />}></Button></Tooltip>
                        </div>
                      }>
                        <div>
                          <Input.TextArea placeholder={getTranslation('Enter your comment...', language)} onChange={(e) => setComment(e.target.value)} />
                          <Button style={{ marginTop: '10px' }} type="primary" size='small' onClick={handleSubmitComment}>{getTranslation("Comment", language)}</Button>
                        </div>
                        {(getCurrentOpenedChat() && getCurrentOpenedChat()?.comments?.length) ?
                          <div>
                            {getCurrentOpenedChat()?.comments?.map((item) => {
                              return (
                                <div className={styles.comment__wrapper}>
                                  <div className='topHeader'>
                                    <div>
                                      <Avatar icon={<UserOutlined />} />
                                      <b style={{ marginLeft: "10px" }}>{item?.user}</b>
                                    </div>
                                    <small>
                                      <TimeAgo datetime={item?.created_at} />
                                    </small>
                                  </div>
                                  <p>{item?.content}</p>
                                </div>
                              )
                            })}
                          </div>
                          :
                          <Empty description={getTranslation("No Comments!", language)} />
                        }
                      </Card>
                    }
                  </Card>
                </Col>
              </Row>
            ))
      )}

      <Drawer
        title={getTranslation("Add Feature Request", language)}
        placement="right"
        onClose={toggleDrawer}
        open={drawerVisible}
        width={600}
      >
        <FeatureRequestForm fetchFeatures={fetchFeatures} closeDrawer={toggleDrawer} /> {/* Feature request form component */}
      </Drawer>
    </div>

  );
};

export default FeatureRequestPage;


